import classNames from 'classnames';
import _ from 'lodash';
import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppBanner from '../../banners/AppBanner';
import ModalContainer from '../../components/ModalContainer';
import ToastContainer from '../../components/ToastContainer';
import OnboardingContainer from '../../onboarding/OnboardingContainer';
import UIActions from '../../ui/UIActions';
import PageMeta from '../../utils/PageMeta';
import Sizes, { SIDEBAR_SHIFT_BREAKPOINT } from '../../utils/responsive/Sizes';
import isSize from '../../utils/responsive/isSize';
import { IsPublicViewProvider } from '../../utils/useIsPublicView';
import { useUserUiTheme } from '../../utils/useUiTheme';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';
import RightPanelContainer from './RightPanelContainer';
import { isFeatureEnabled } from '../../utils/features';

const SIDEBAR_DEBOUNCE_TIMEOUT = 100;

const useSidebarState = ({ hasSidebar }: { hasSidebar: boolean }) => {
  const location = useLocation();
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const dispatch = useAppDispatch();

  const [
    shouldShiftContentForNewPostLayout,
    setShouldShiftContentForNewPostLayout,
  ] = useState(
    () =>
      isFeatureEnabled('new_post_layout') &&
      window.innerWidth < SIDEBAR_SHIFT_BREAKPOINT,
  );

  useEffect(() => {
    if (!isFeatureEnabled('new_post_layout')) return;

    if (window.innerWidth < SIDEBAR_SHIFT_BREAKPOINT) {
      dispatch(UIActions.toggleSidebar(false));
    }
  }, [location, dispatch]);

  useEffect(() => {
    if (
      !isFeatureEnabled('new_post_layout') &&
      hasSidebar &&
      isSize(Sizes.SM)
    ) {
      dispatch(UIActions.toggleSidebar(false));
    }
  }, [hasSidebar, location, dispatch]);

  useEffect(() => {
    if (!isFeatureEnabled('new_post_layout')) return undefined;

    let previousWidth = window.innerWidth;
    const onResize = _.debounce(() => {
      if (
        previousWidth >= SIDEBAR_SHIFT_BREAKPOINT &&
        window.innerWidth < SIDEBAR_SHIFT_BREAKPOINT
      ) {
        if (isSidebarOpen) {
          dispatch(UIActions.toggleSidebar(false));
        }
        setShouldShiftContentForNewPostLayout(true);
      } else if (
        previousWidth < SIDEBAR_SHIFT_BREAKPOINT &&
        window.innerWidth >= SIDEBAR_SHIFT_BREAKPOINT
      ) {
        if (!isSidebarOpen) {
          dispatch(UIActions.toggleSidebar(true));
        }
        setShouldShiftContentForNewPostLayout(false);
      }
      previousWidth = window.innerWidth;
    }, SIDEBAR_DEBOUNCE_TIMEOUT);

    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, [isSidebarOpen, dispatch]);

  return {
    isSidebarOpen: hasSidebar && isSidebarOpen,
    shouldShiftContentForNewPostLayout,
  };
};

export interface PageLayoutElements {
  sidebar?: JSX.Element;
  topbar?: JSX.Element;
  helpMenu?: JSX.Element;
}

interface PageLayoutProps {
  elements: PageLayoutElements;
  routeName: string;
}

const PageLayout = ({
  elements: { sidebar, topbar, helpMenu },
  routeName,
}: PageLayoutProps) => {
  const theme = useUserUiTheme();

  useLayoutEffect(() => {
    if (theme) {
      document.body.setAttribute('data-theme', theme.toLowerCase());
    }

    return () => {
      document.body.removeAttribute('data-theme');
    };
  }, [theme]);

  const dispatch = useAppDispatch();

  const { isSidebarOpen, shouldShiftContentForNewPostLayout } = useSidebarState(
    {
      hasSidebar: !!sidebar,
    },
  );

  const classes = classNames('app-root', {
    'should-shift-content-for-new-post-layout':
      shouldShiftContentForNewPostLayout,
    sidebar: isSidebarOpen,
    'no-sidebar': !isSidebarOpen,
  });

  return (
    <IsPublicViewProvider>
      <div id={`${routeName}-view`} className={classes}>
        <PageMeta title={`${_.startCase(routeName)} - Slab`} />
        <AppBanner />
        <ModalContainer />
        <ToastContainer />
        {sidebar}
        <main id="main">
          {topbar}
          <div id="inner">
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
            <RightPanelContainer />
            {helpMenu}
          </div>
        </main>
        {isFeatureEnabled('new_post_layout') && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            id="overlay"
            onClick={() => {
              dispatch(UIActions.toggleSidebar(false));
            }}
          />
        )}
        <OnboardingContainer />
      </div>
    </IsPublicViewProvider>
  );
};

export default PageLayout;
